"use client";

import { Car, MapPin, TrainFront as Train } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Office() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const hours = [{
    days: "MONDAY–THURSDAY",
    time: "09:00 – 18:00"
  }, {
    days: "FRIDAY",
    time: "09:00 – 15:00"
  }, {
    days: "WEEKENDS",
    time: "By appointment"
  }];
  const directionLinks = [{
    label: "Get Directions",
    icon: MapPin,
    to: "https://maps.google.com/?q=Lovejoy+Company+Financial+Center"
  }, {
    label: "Public Transit",
    icon: Train,
    to: "/TaxResourcesPage"
  }, {
    label: "Parking",
    icon: Car,
    to: "/TaxResourcesPage"
  }];
  return <section data-section-id="4718" id="our-office" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
          {}
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="relative h-[420px] md:h-[560px] lg:h-[640px] w-full overflow-hidden border border-border bg-card">
              {}
              <div className="absolute inset-0" style={{
              background: "linear-gradient(to bottom right, hsl(0 0% 98%), hsl(0 0% 95%))"
            }} aria-hidden="true" />
              {}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 700 640" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                {}
                <path d="M0 320 Q 200 280 400 340 T 700 320" stroke="hsl(220 10% 40%)" strokeWidth="1" fill="none" opacity="0.2" />
                <path d="M0 140 L 700 180" stroke="hsl(220 10% 40%)" strokeWidth="1" fill="none" opacity="0.2" />
                <path d="M340 0 L 360 640" stroke="hsl(220 10% 40%)" strokeWidth="1" fill="none" opacity="0.2" strokeDasharray="6 8" />
              </svg>

              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full flex flex-col items-center">
                <div className="relative">
                  <div className="absolute inset-0 animate-ping" style={{
                  background: "hsl(72 100% 45% / 0.4)"
                }} />
                  <div className="relative w-5 h-5 border-2" style={{
                  background: "hsl(72 100% 45%)",
                  borderColor: "hsl(220 20% 4%)"
                }} />
                </div>
                <div className="w-px h-6 bg-secondary" aria-hidden="true" />
                <div className="mt-2 px-3 py-1 text-[10px] font-mono tracking-[0.2em] uppercase" style={{
                background: "hsl(220 20% 4%)",
                color: "hsl(0 0% 98%)"
              }}>
                  LOVEJOY HQ
                </div>
              </div>

              {}
              <div className="absolute top-6 right-6 w-12 h-12 border border-border flex items-center justify-center bg-background/40 backdrop-blur-sm">
                <div className="flex flex-col items-center">
                  <span className="text-[9px] font-mono tracking-widest text-muted-foreground">N</span>
                  <div className="w-px h-3 bg-primary" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 px-6 md:px-10 lg:pr-12 lg:pl-4 flex flex-col justify-center transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px bg-secondary" aria-hidden="true" />
              <span className="font-mono text-xs tracking-[0.3em] uppercase text-secondary">
                Consultation
              </span>
            </div>

            {}
            <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] font-semibold text-foreground mb-10">
              Visit our *office*.
            </h2>

            {}
            <address className="not-italic font-default text-lg md:text-xl leading-relaxed text-foreground/90 mb-8 space-y-1">
              <div>100 Financial Plaza</div>
              <div className="text-muted-foreground">Downtown Business District</div>
              <div>Chicago, Illinois</div>
            </address>

            {}
            <div className="h-px w-full bg-border mb-8" aria-hidden="true" />

            {}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-y-4 gap-x-8 mb-8">
              {hours.map((row, i) => <div key={i} data-index={i} className="contents">
                  <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground self-center">
                    {row.days}
                  </span>
                  <span className="font-default text-base text-foreground sm:text-right">
                    {row.time}
                  </span>
                </div>)}
            </div>

            {}
            <p className="font-default italic text-sm text-muted-foreground mb-10 leading-relaxed">
              Strategic planning sessions available by appointment.
            </p>

            {}
            <div className="flex flex-col gap-6">
              <Button asChild className="px-8 py-6 h-auto justify-start font-default text-lg bg-accent text-accent-foreground hover:shadow-md transition-all">
                <Link to="#">
                  Schedule Consultation
                </Link>
              </Button>

              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {directionLinks.map((link, i) => {
                const Icon = link.icon;
                return <Link key={i} data-index={i} to={link.to} className="flex items-center gap-2 text-xs font-mono tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors">
                      <Icon className="w-3.5 h-3.5 text-accent" />
                      <span>{link.label}</span>
                    </Link>;
              })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="pointer-events-none absolute bottom-0 right-0 font-default font-bold leading-none select-none" style={{
      fontSize: "clamp(200px, 28vw, 420px)",
      color: "hsl(220 20% 4%)",
      opacity: 0.03,
      transform: "translate(15%, 20%)"
    }} aria-hidden="true">
        L
      </div>
    </section>;
}